import { useEffect, useState } from "react";
import { api } from "../lib/config";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Loader2, Plus, Trash2, FileText, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";

/**
 * SendQuoteDialog — admin builds and sends a quote.
 *
 * Props:
 *   open: boolean
 *   onOpenChange: (bool) => void
 *   customer: { user_id, name, email, phone, default_address } | null
 *   onSent: () => void   // called after successful send (parent refreshes lists)
 *
 * Photos are stored as base64 data URLs directly on the quote document (per
 * the user's choice 2a in the planning step). 5 MB cap per image to keep the
 * Mongo doc small.
 */
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

function PhotoStrip({ list, setList, label, onUpload }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-gray-500">{label}</Label>
      <div className="mt-1.5 flex items-center gap-2 flex-wrap">
        {list.map((p, i) => (
          <div key={i} className="relative w-16 h-16 rounded-lg overflow-hidden border">
            <img src={p} alt="" className="w-full h-full object-cover" />
            <button onClick={() => setList(list.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-rose-500 text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
          </div>
        ))}
        <label className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 text-gray-500 flex items-center justify-center cursor-pointer hover:border-emerald-400">
          <ImageIcon className="w-5 h-5" />
          <input type="file" accept="image/*" multiple className="hidden" onChange={onUpload} />
        </label>
      </div>
    </div>
  );
}

export default function SendQuoteDialog({ open, onOpenChange, customer, onSent }) {
  const [services, setServices] = useState([]);
  const [title, setTitle] = useState("Lawn Care Services");
  const [notes, setNotes] = useState("");
  const [estimatedCompletion, setEstimatedCompletion] = useState("");
  const [items, setItems] = useState([{ service_key: "", name: "", price: "", notes: "", estimated_hours: "" }]);
  const [beforePhotos, setBeforePhotos] = useState([]);
  const [afterPhotos, setAfterPhotos] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/services?include_quote_only=true").then(r => setServices(r.data.services || [])).catch(() => {});
    // Reset form whenever it reopens.
    setTitle("Lawn Care Services");
    setNotes("");
    setEstimatedCompletion("");
    setItems([{ service_key: "", name: "", price: "", notes: "", estimated_hours: "" }]);
    setBeforePhotos([]);
    setAfterPhotos([]);
    setPhotos([]);
  }, [open, customer]);

  const total = items.reduce((s, it) => s + (parseFloat(it.price) || 0), 0);

  const addItem = () => setItems([...items, { service_key: "", name: "", price: "", notes: "", estimated_hours: "" }]);
  const removeItem = (i) => setItems(items.length === 1 ? items : items.filter((_, idx) => idx !== i));
  const updateItem = (i, patch) => setItems(items.map((it, idx) => idx === i ? { ...it, ...patch } : it));

  const onServicePick = (i, key) => {
    const svc = services.find(s => s.key === key);
    updateItem(i, { service_key: key, name: svc?.name || "" });
  };

  const onPhotoUpload = (e, target) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (f.size > MAX_PHOTO_BYTES) return toast.error(`${f.name} is over 5MB — pick a smaller image`);
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target?.result;
        if (target === "before") setBeforePhotos((p) => [...p, dataUrl]);
        if (target === "after") setAfterPhotos((p) => [...p, dataUrl]);
        if (target === "general") setPhotos((p) => [...p, dataUrl]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = async (sendImmediately) => {
    if (!customer) return;
    const cleanedItems = items
      .map((it) => ({
        service_key: it.service_key || null,
        name: it.name || (services.find(s => s.key === it.service_key)?.name) || "Service",
        price: parseFloat(it.price) || 0,
        notes: it.notes || null,
        estimated_hours: it.estimated_hours ? parseFloat(it.estimated_hours) : null,
      }))
      // iter83: keep every item with a name (negatives are allowed for
      // discounts). Reject only unnamed rows.
      .filter((it) => (it.name || "").trim().length > 0);
    if (cleanedItems.length === 0) return toast.error("Add at least one line item");
    // iter83: computed total must be > $0 before we can actually send.
    // Saving as draft is still allowed at any value so the admin can
    // iterate on line items.
    const totalToSend = cleanedItems.reduce((s, it) => s + (it.price || 0), 0);
    if (sendImmediately && totalToSend <= 0) {
      return toast.error("Please enter a quote amount before sending this quote.");
    }
    setSubmitting(true);
    try {
      const { data: created } = await api.post("/admin/quotes", {
        user_id: customer.user_id || null,
        customer_name: customer.name || customer.email || "Customer",
        customer_email: customer.email || null,
        customer_phone: customer.phone || null,
        address: customer.default_address || customer.address || null,
        title,
        items: cleanedItems,
        notes: notes || null,
        estimated_completion: estimatedCompletion || null,
        photos,
        before_photos: beforePhotos,
        after_photos: afterPhotos,
      });
      if (sendImmediately) {
        await api.post(`/admin/quotes/${created.quote_id}/send`);
        toast.success(`Quote sent to ${customer.name}`);
      } else {
        toast.success("Quote saved as draft");
      }
      onSent && onSent();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create quote");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-quote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-[#047857]" /> Send quote to {customer?.name || "customer"}</DialogTitle>
          <DialogDescription>Build a recommendation. Saves as draft unless you send.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-1">
          <div>
            <Label>Quote title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="quote-title" />
          </div>

          <div>
            <Label>Line items</Label>
            <div className="mt-1 space-y-3">
              {items.map((it, i) => (
                <div key={i} className="border border-gray-200 rounded-xl p-3" data-testid={`quote-line-${i}`}>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <select value={it.service_key} onChange={(e) => onServicePick(i, e.target.value)} className="w-full text-sm border rounded-md h-9 px-2">
                        <option value="">— pick a service —</option>
                        {services.map((s) => <option key={s.key} value={s.key}>{s.name}{s.quote_only ? " *" : ""}</option>)}
                      </select>
                      <Input className="mt-1.5" placeholder="Custom service name (optional)" value={it.name} onChange={(e) => updateItem(i, { name: e.target.value })} />
                    </div>
                    <div>
                      <Input type="number" step="0.01" placeholder="$ Price" value={it.price} onChange={(e) => updateItem(i, { price: e.target.value })} data-testid={`quote-price-${i}`} />
                      <Input type="number" min={0} step="0.5" placeholder="~hrs" value={it.estimated_hours} onChange={(e) => updateItem(i, { estimated_hours: e.target.value })} className="mt-1.5" />
                    </div>
                  </div>
                  <Input className="mt-2" placeholder="Notes for this item (optional)" value={it.notes} onChange={(e) => updateItem(i, { notes: e.target.value })} />
                  {items.length > 1 && (
                    <button onClick={() => removeItem(i)} className="mt-2 text-xs text-rose-600 inline-flex items-center"><Trash2 className="w-3 h-3 mr-1" /> Remove line</button>
                  )}
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={addItem} className="mt-2 rounded-full" data-testid="quote-add-line"><Plus className="w-3.5 h-3.5 mr-1" /> Add line item</Button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Est. completion</Label>
              <Input placeholder="e.g. 2-3 days" value={estimatedCompletion} onChange={(e) => setEstimatedCompletion(e.target.value)} />
            </div>
            <div>
              <Label>Total</Label>
              <div className={`h-10 flex items-center font-extrabold text-2xl tabular-nums ${total <= 0 ? "text-rose-600" : "text-[#047857]"}`} data-testid="quote-total-display">${total.toFixed(2)}</div>
              {total <= 0 && (
                <div className="text-[11px] text-rose-600 font-bold mt-0.5" data-testid="quote-total-warning">
                  Enter a quote amount before sending
                </div>
              )}
            </div>
          </div>

          <div>
            <Label>Notes to customer</Label>
            <Textarea rows={3} placeholder="Anything you'd like them to know" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <PhotoStrip list={beforePhotos} setList={setBeforePhotos} label="Before" onUpload={(e) => onPhotoUpload(e, "before")} />
          <PhotoStrip list={photos} setList={setPhotos} label="Photos" onUpload={(e) => onPhotoUpload(e, "general")} />
          <PhotoStrip list={afterPhotos} setList={setAfterPhotos} label="After" onUpload={(e) => onPhotoUpload(e, "after")} />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => submit(false)} disabled={submitting} className="rounded-full" data-testid="quote-save-draft">Save as draft</Button>
          <Button onClick={() => submit(true)} disabled={submitting || total <= 0} className="rounded-full bg-[#047857] hover:bg-[#065F46] font-bold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="quote-send" title={total <= 0 ? "Enter a quote amount before sending" : undefined}>
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send quote"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
